import React from 'react'

interface User {
  id: number
  first_name: string
  last_name: string
  email?: string
  profile_picture_url?: string
  assignment_type?: 'primary' | 'collaborator'
}

interface UserAvatarStackProps {
  users: User[]
  maxVisible?: number
  size?: 'sm' | 'md' | 'lg'
  showTooltip?: boolean
  className?: string
}

const UserAvatarStack: React.FC<UserAvatarStackProps> = ({ 
  users, 
  maxVisible = 3, 
  size = 'md',
  showTooltip = true,
  className = ''
}) => {
  if (!users || users.length === 0) {
    return null
  }

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  }

  const overlaps = {
    sm: '-ml-2',
    md: '-ml-3',
    lg: '-ml-4'
  }

  const visibleUsers = users.slice(0, maxVisible)
  const remainingCount = Math.max(0, users.length - maxVisible)

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getTooltipText = () => {
    if (users.length === 1) {
      const user = users[0]
      return `${user.first_name} ${user.last_name}${user.assignment_type === 'primary' ? ' (Primary)' : ''}`
    }
    
    return users.map(user => 
      `${user.first_name} ${user.last_name}${user.assignment_type === 'primary' ? ' (Primary)' : ''}`
    ).join(', ')
  }

  const getUserBorderColor = (user: User) => {
    return user.assignment_type === 'primary' 
      ? 'border-gray-900 border-2' 
      : 'border-gray-300 dark:border-gray-600 border'
  }

  return (
    <div 
      className={`flex items-center relative ${className}`}
      title={showTooltip ? getTooltipText() : undefined}
    >
      {visibleUsers.map((user, index) => (
        <div
          key={user.id}
          className={`
            ${sizeClasses[size]} 
            ${index > 0 ? overlaps[size] : ''} 
            ${getUserBorderColor(user)}
            rounded-full 
            bg-white 
            dark:bg-gray-800 
            flex 
            items-center 
            justify-center 
            relative 
            z-${10 - index}
            hover:z-50
            transition-all
            duration-200
            hover:scale-110
            cursor-pointer
            group
          `}
          style={{ zIndex: 10 - index }}
        >
          {user.profile_picture_url ? (
            <img
              src={user.profile_picture_url}
              alt={`${user.first_name} ${user.last_name}`}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <div className={`
              w-full 
              h-full 
              rounded-full 
              bg-gradient-to-r 
              ${user.assignment_type === 'primary' 
                ? 'from-gray-800 to-gray-900' 
                : 'from-gray-500 to-gray-600'
              }
              flex 
              items-center 
              justify-center 
              text-white 
              font-semibold
            `}>
              {getInitials(user.first_name, user.last_name)}
            </div>
          )}
          
          {/* Tooltip on hover for individual user */}
          {showTooltip && (
            <div className="
              absolute 
              bottom-full 
              left-1/2 
              transform 
              -translate-x-1/2 
              mb-2 
              px-2 
              py-1 
              bg-gray-900 
              text-white 
              text-xs 
              rounded 
              whitespace-nowrap 
              opacity-0 
              group-hover:opacity-100 
              transition-opacity 
              pointer-events-none
              z-50
            ">
              {user.first_name} {user.last_name}
              {user.assignment_type === 'primary' && ' (Primary)'}
              <div className="
                absolute 
                top-full 
                left-1/2 
                transform 
                -translate-x-1/2 
                border-l-2 
                border-r-2 
                border-t-2 
                border-transparent 
                border-t-gray-900
              "></div>
            </div>
          )}
        </div>
      ))}
      
      {/* Show remaining count if there are more users */}
      {remainingCount > 0 && (
        <div
          className={`
            ${sizeClasses[size]} 
            ${overlaps[size]}
            border-gray-300 
            dark:border-gray-600 
            border
            rounded-full 
            bg-gray-100 
            dark:bg-gray-700 
            flex 
            items-center 
            justify-center 
            text-gray-600 
            dark:text-gray-300 
            font-semibold
            relative
            cursor-pointer
            hover:bg-gray-200
            dark:hover:bg-gray-600
            transition-colors
            group
          `}
          title={showTooltip ? `+${remainingCount} more` : undefined}
        >
          +{remainingCount}
          
          {/* Tooltip for remaining count */}
          {showTooltip && (
            <div className="
              absolute 
              bottom-full 
              left-1/2 
              transform 
              -translate-x-1/2 
              mb-2 
              px-2 
              py-1 
              bg-gray-900 
              text-white 
              text-xs 
              rounded 
              whitespace-nowrap 
              opacity-0 
              group-hover:opacity-100 
              transition-opacity 
              pointer-events-none
              z-50
            ">
              {users.slice(maxVisible).map(user => 
                `${user.first_name} ${user.last_name}`
              ).join(', ')}
              <div className="
                absolute 
                top-full 
                left-1/2 
                transform 
                -translate-x-1/2 
                border-l-2 
                border-r-2 
                border-t-2 
                border-transparent 
                border-t-gray-900
              "></div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UserAvatarStack
